import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { NGXLogger as LoggerService } from "ngx-logger";
import { Observable, Subject } from 'rxjs';
import { paths } from 'functions-lib';
import { HeaderData, isResolvableVoteSlug, isSafeRtdbKey, proposeVoteSlug, validateVoteSlug, VOTE_SLUG_MAX_ALIASES } from 'whocan-lib';
import { CurrentPollService } from './current-poll.service';
import { PathService } from './path.service';
import { HeaderService } from '../shared/poll-header/header.service';
import { ServerTimeService } from '../../shared/services/server-time.service';
import { isPermissionDenied } from '../../shared/services/db-errors';

/** proposals per ensureForCurrentPoll before the poll stays without a slug (concept §3: max. 3 attempts) */
export const VOTE_SLUG_PROPOSAL_ATTEMPTS = 3;

/**
 * The index write of a slug was refused: someone holds it (`!data.exists()` in the rules). Expected
 * between the availability check and the write - a user state ("taken"), never an error report.
 */
export class VoteSlugTakenError extends Error {
    constructor() {
        super('vote slug is taken');
        this.name = 'VoteSlugTakenError';
        Object.setPrototypeOf(this, VoteSlugTakenError.prototype);
    }
}

/** an index entry `whoo/pollSlugs/{slug}` */
interface PollSlugEntry {
    ownerId: string;
    pollId: string;
    createdAt: number;
}

type VoteSlugAliases = HeaderData['voteSlugAliases'];

/**
 * Participant link slug of the CURRENT poll (concept-custom-participant-links-2026-10-28 §4, workorder S3):
 * the index `whoo/pollSlugs/{slug}` plus the header fields `voteSlug` / `voteSlugAliases`. All slug logic
 * (normalize, validate, propose, alternatives) comes from whocan-lib - nothing of it is duplicated here.
 *
 * Two rules the database enforces and this service has to follow:
 * - **index first, header second**, never one multi-path update over both (rules R18 - `root` is the state
 *   before the write, the header validation would not see the new index entry);
 * - **never delete an index entry the header still points to**: every later full header write
 *   (`HeaderService.persist`) would fail its validation. A change therefore runs new index → header → delete
 *   of the dropped alias.
 *
 * Resolving (route) uses `isResolvableVoteSlug` (format + hard list), creating and changing
 * `validateVoteSlug === 'ok'` (blocks the soft list too) - never the other way round (report S2 §6.1).
 *
 * The header fields are written with a targeted update, not with `HeaderService.persist()`: a new address is
 * no content change of the poll (no `updatedAt`). The in-memory header is updated right after every write -
 * persist writes the whole header from memory, a stale `voteSlug` there would write the old slug back.
 */
@Injectable({
    providedIn: 'root'
})
export class VoteSlugService {
    /** the running ensure per poll - the share panel, the share dialog and persistNewPoll may ask at once */
    private ensureInFlight: { pollKey: string; promise: Promise<void> } | null = null;
    private readonly voteSlugChangedSubject = new Subject<void>();

    /** emits after a slug of the current poll was created or changed - hosts that render the url (qr) refresh */
    public readonly voteSlugChanged$: Observable<void> = this.voteSlugChangedSubject.asObservable();

    constructor(
        private logger: LoggerService,
        private afd: AngularFireDatabase,
        private pathService: PathService,
        private currentPollService: CurrentPollService,
        private headerService: HeaderService,
        private serverTimeService: ServerTimeService,
    ) { }

    /**
     * Reads `pollSlugs/{slug}`; false when free. A value that may not be resolved counts as taken - it can
     * not be created either, and an invalid key would throw synchronously in `ref()`.
     */
    public async isTaken(slug: string): Promise<boolean> {
        if (!isResolvableVoteSlug(slug)) return true;
        const snapshot = await this.afd.database.ref(this.indexPath(slug)).once('value');
        return snapshot.exists();
    }

    /** resolution for the slug route: the poll behind the slug, or null. Throws on a failed read (caller retries). */
    public async resolve(slug: string): Promise<{ ownerId: string; pollId: string } | null> {
        if (!isResolvableVoteSlug(slug)) return null;
        const snapshot = await this.afd.database.ref(this.indexPath(slug)).once('value');
        const entry: Partial<PollSlugEntry> | null = snapshot.val();
        if (!entry || typeof entry.ownerId !== 'string' || typeof entry.pollId !== 'string'
            || !isSafeRtdbKey(entry.ownerId) || !isSafeRtdbKey(entry.pollId)) {
            return null;
        }
        return { ownerId: entry.ownerId, pollId: entry.pollId };
    }

    /**
     * Makes sure the current poll has a slug - idempotent and single-flight per poll. Only for an organizer
     * (`isOwner`), never for a sample poll, only with the header of this poll loaded and without a slug.
     * Proposal → index → header; a taken proposal gets a new random part, after three attempts the poll
     * stays without a slug (the surfaces fall back to the id url). Never throws.
     */
    public ensureForCurrentPoll(): Promise<void> {
        const ownerId = this.currentPollService.ownerId;
        const pollId = this.currentPollService.pollId;
        const pollKey = ownerId + '/' + pollId;
        if (this.ensureInFlight?.pollKey === pollKey) {
            return this.ensureInFlight.promise;
        }
        const promise = this.ensureOnce(ownerId, pollId).finally(() => {
            if (this.ensureInFlight?.promise === promise) this.ensureInFlight = null;
        });
        this.ensureInFlight = { pollKey, promise };
        return promise;
    }

    private async ensureOnce(ownerId: string, pollId: string): Promise<void> {
        try {
            if (!this.canWriteSlug(ownerId, pollId)) return;
            const header = this.headerService.headerClass;
            if (header.voteSlug) return;
            for (let attempt = 1; attempt <= VOTE_SLUG_PROPOSAL_ATTEMPTS; attempt++) {
                const slug = proposeVoteSlug(header.pollTitle);
                if (validateVoteSlug(slug) !== 'ok') continue; // a soft reserved title without suffix can not happen, but never write one
                try {
                    await this.createIndexEntry(slug, ownerId, pollId);
                } catch (error) {
                    if (error instanceof VoteSlugTakenError) continue;
                    throw error;
                }
                try {
                    await this.updateHeaderFields(ownerId, pollId, { voteSlug: slug });
                } catch (error) {
                    // the index entry points at the poll without the header knowing it - take it back, best effort
                    await this.removeIndexEntryOf(slug, ownerId, pollId);
                    throw error;
                }
                this.applyToHeader(ownerId, pollId, slug, header.voteSlugAliases);
                this.logger.log('VoteSlugService: slug created', ownerId, pollId, 'attempt', attempt);
                this.voteSlugChangedSubject.next();
                return;
            }
            this.logger.warn('VoteSlugService: no free slug after ' + VOTE_SLUG_PROPOSAL_ATTEMPTS + ' proposals, the poll keeps the id url', ownerId, pollId);
        } catch (error) {
            // no user error: the poll works without a slug, the participant link is the id url
            this.logger.warn('VoteSlugService: slug not created', ownerId, pollId, error?.code ?? error?.message ?? error);
        }
    }

    /**
     * Switches the current poll to a slug the organizer chose. `validateVoteSlug === 'ok'` → index entry →
     * header (`voteSlug` new, aliases rotated: old slug to 0, old 0 to 1, old 1 drops out) → index entry of
     * the dropped alias deleted → in-memory header. One of the poll's own aliases is taken back without an
     * index write and without dropping anything. Throws VoteSlugTakenError when the index write is refused.
     */
    public async changeForCurrentPoll(newSlug: string): Promise<void> {
        const ownerId = this.currentPollService.ownerId;
        const pollId = this.currentPollService.pollId;
        if (validateVoteSlug(newSlug) !== 'ok') {
            throw new Error('VoteSlugService.changeForCurrentPoll: invalid slug');
        }
        if (!this.canWriteSlug(ownerId, pollId)) {
            throw new Error('VoteSlugService.changeForCurrentPoll: no organizer of a loaded poll');
        }
        const header = this.headerService.headerClass;
        const current = header.voteSlug;
        if (newSlug === current) return;
        const alias0 = aliasAt(header.voteSlugAliases, 0);
        const alias1 = aliasAt(header.voteSlugAliases, 1);
        const isOwnAlias = newSlug === alias0 || newSlug === alias1;
        // the youngest first; the new slug never sits in its own alias list
        const remaining = [current, alias0, alias1].filter(slug => !!slug && slug !== newSlug);
        const nextAliases = remaining.slice(0, VOTE_SLUG_MAX_ALIASES);
        const dropped = remaining.slice(VOTE_SLUG_MAX_ALIASES);

        if (!isOwnAlias) {
            await this.createIndexEntry(newSlug, ownerId, pollId);
        }
        try {
            await this.updateHeaderFields(ownerId, pollId, {
                voteSlug: newSlug,
                'voteSlugAliases/0': nextAliases[0] ?? null,
                'voteSlugAliases/1': nextAliases[1] ?? null,
            });
        } catch (error) {
            if (!isOwnAlias) await this.removeIndexEntryOf(newSlug, ownerId, pollId);
            throw error;
        }
        this.applyToHeader(ownerId, pollId, newSlug, toAliases(nextAliases));
        this.voteSlugChangedSubject.next();
        // only now: the header no longer points at the dropped slugs
        for (const slug of dropped) {
            await this.removeIndexEntryOf(slug, ownerId, pollId);
        }
    }

    /**
     * The address that stops working when the current poll switches to `newSlug` (the oldest alias of a full
     * alias list), or null - for the alias limit hint. Taking back one of the own aliases drops nothing.
     */
    public slugDroppedOnChange(newSlug?: string): string | null {
        const header = this.headerService.headerClass;
        if (!header?.voteSlug) return null;
        const alias0 = aliasAt(header.voteSlugAliases, 0);
        const alias1 = aliasAt(header.voteSlugAliases, 1);
        if (newSlug && (newSlug === header.voteSlug || newSlug === alias0 || newSlug === alias1)) return null;
        return alias0 && alias1 ? alias1 : null;
    }

    /** the current slug or one of the aliases of the current poll - free for this poll although the index has it */
    public isOwnSlug(slug: string): boolean {
        const header = this.headerService.headerClass;
        if (!slug || !header?.headerData) return false;
        return slug === header.voteSlug
            || slug === aliasAt(header.voteSlugAliases, 0)
            || slug === aliasAt(header.voteSlugAliases, 1);
    }

    /**
     * Deletes the index entries of voteSlug and both aliases (deletePoll). Without a header passed in the poll's
     * header is read once. Every failure is logged on its own and never thrown - the poll delete goes on, the
     * orphan sweep (S4) is the net.
     */
    public async removeAllForPoll(ownerId: string, pollId: string, header?: HeaderData | null): Promise<void> {
        let headerData = header;
        if (headerData === undefined) {
            try {
                const snapshot = await this.afd.database.ref(this.pathService.getHeaderPath(ownerId, pollId)).once('value');
                headerData = snapshot.val();
            } catch (error) {
                this.logger.warn('VoteSlugService.removeAllForPoll: header not read', ownerId, pollId, error?.code ?? error?.message);
                return;
            }
        }
        const slugs = [headerData?.voteSlug, aliasAt(headerData?.voteSlugAliases, 0), aliasAt(headerData?.voteSlugAliases, 1)]
            .filter((slug): slug is string => typeof slug === 'string' && isResolvableVoteSlug(slug));
        for (const slug of slugs) {
            await this.removeIndexEntryOf(slug, ownerId, pollId);
        }
    }

    /** organizer of exactly this, loaded, non-sample poll - the set the rules accept for index and header */
    private canWriteSlug(ownerId: string, pollId: string): boolean {
        const header = this.headerService.headerClass;
        if (!ownerId || !pollId || !this.currentPollService.isCurrentPoll(ownerId, pollId)) return false;
        if (!this.currentPollService.isOwner || this.currentPollService.isSampleMode) return false;
        if (!header?.headerData || header.isSamplePoll) return false;
        // the same identity check as HeaderService.persist: a header of another poll is never touched
        return (!header.ownerId || header.ownerId === ownerId) && (!header.pollId || header.pollId === pollId);
    }

    private indexPath(slug: string): string {
        return paths.pollSlugs + '/' + slug;
    }

    /** `set` on a free slug; a refusal is the rules' `!data.exists()` → VoteSlugTakenError */
    private async createIndexEntry(slug: string, ownerId: string, pollId: string): Promise<void> {
        const entry: PollSlugEntry = { ownerId, pollId, createdAt: this.serverTimeService.now() };
        try {
            await this.afd.database.ref(this.indexPath(slug)).set(entry);
        } catch (error) {
            if (isPermissionDenied(error)) throw new VoteSlugTakenError();
            throw error;
        }
    }

    /** only the two slug fields - no updatedAt, no full header */
    private async updateHeaderFields(ownerId: string, pollId: string, fields: { [path: string]: string | null }): Promise<void> {
        await this.afd.database.ref(this.pathService.getHeaderPath(ownerId, pollId)).update(fields);
    }

    /** deletes `pollSlugs/{slug}` when it points at this poll; a failure is a warn, never thrown */
    private async removeIndexEntryOf(slug: string, ownerId: string, pollId: string): Promise<void> {
        try {
            const ref = this.afd.database.ref(this.indexPath(slug));
            const entry: Partial<PollSlugEntry> | null = (await ref.once('value')).val();
            if (!entry) return;
            if (entry.ownerId !== ownerId || entry.pollId !== pollId) {
                this.logger.warn('VoteSlugService: index entry belongs to another poll, not deleted', ownerId, pollId);
                return;
            }
            await ref.remove();
        } catch (error) {
            this.logger.warn('VoteSlugService: index entry not deleted', ownerId, pollId, error?.code ?? error?.message);
        }
    }

    /**
     * Brings the in-memory header in line with the database. A header that had no unsaved edits stays clean:
     * the slug fields are no content edit, the vote preview must not auto-save because of them.
     */
    private applyToHeader(ownerId: string, pollId: string, voteSlug: string, aliases: VoteSlugAliases): void {
        const header = this.headerService.headerClass;
        if (!header?.headerData || (header.ownerId && header.ownerId !== ownerId) || (header.pollId && header.pollId !== pollId)) {
            return; // the poll was switched meanwhile - load() reads the new state from the database
        }
        const wasClean = !header.hasChangedSinceLastPersitance();
        header.voteSlug = voteSlug;
        header.voteSlugAliases = aliases;
        if (wasClean) header.resetDataSinceLastPersistence();
    }
}

/** `voteSlugAliases` comes back from the database as an array when the keys are 0 and 1 - both forms read alike */
function aliasAt(aliases: VoteSlugAliases | string[] | null | undefined, index: 0 | 1): string | undefined {
    const value = (aliases as Record<number, string> | null | undefined)?.[index];
    return typeof value === 'string' && value ? value : undefined;
}

function toAliases(slugs: string[]): VoteSlugAliases {
    if (slugs.length === 0) return undefined;
    const aliases: VoteSlugAliases = {};
    if (slugs[0]) aliases[0] = slugs[0];
    if (slugs[1]) aliases[1] = slugs[1];
    return aliases;
}
